import { normalizedSpeechText, plainSpeechText } from "./tts-reply-text";
import { splitSpeechSegments } from "./tts-speech-segments";

// Function words carry no content: two sentences sharing only these say
// different things. English and German, the languages Denker speaks most.
const STOPWORDS = new Set(
  (
    "a an the and or but if then so of to in on at by for from with as is are " +
    "was were be been being it its this that these those there here i you he " +
    "she we they me him her us them my your our their s d ll re ve t not no do " +
    "does did has have had will would can could should may might just also " +
    "very really about into than which who what when where how all any some " +
    "der die das ein eine einen einem einer und oder aber wenn dann mit von " +
    "zu im in am an auf für ist sind war waren es ich du er sie wir ihr den " +
    "dem des nicht kein keine auch noch nur sehr so wie was wer"
  ).split(" "),
);

/**
 * A sentence repeats an already spoken one when at least 60% of its content
 * words appear in that spoken sentence. A paraphrased recap keeps most of its
 * nouns and verbs; a new recommendation brings mostly new ones. With three
 * content words or fewer one shared word swings the ratio, so a short
 * sentence counts as repeated only when it reads exactly the same.
 */
const REPEAT_OVERLAP = 0.6;
const SHORT_SENTENCE_WORDS = 3;

function sentences(text: string): string[] {
  return splitSpeechSegments(plainSpeechText(text), true).map(
    (segment) => segment.text,
  );
}

function contentWords(sentence: string): string[] {
  return normalizedSpeechText(sentence)
    .split(" ")
    .filter((word) => word && !STOPWORDS.has(word));
}

function repeats(sentence: string, spoken: string): boolean {
  const words = [...new Set(contentWords(sentence))];
  if (words.length <= SHORT_SENTENCE_WORDS) {
    return normalizedSpeechText(sentence) === normalizedSpeechText(spoken);
  }
  const spokenWords = new Set(contentWords(spoken));
  const shared = words.filter((word) => spokenWords.has(word)).length;
  return shared / words.length >= REPEAT_OVERLAP;
}

/**
 * `text` without the sentences already heard, in order: one that reads
 * exactly like a `spoken` sentence, or that repeats a `narrated` one. Only
 * narration is matched loosely: the reply recaps it in other words, while a
 * reply's own sentences share topic words without saying the same thing.
 * Unchanged when nothing repeats; empty when everything does.
 */
export function withoutHeardSentences(
  text: string,
  heard: { spoken: string[]; narrated: string[] },
): string {
  const spoken = new Set(
    heard.spoken.flatMap(sentences).map(normalizedSpeechText),
  );
  const narrated = heard.narrated.flatMap(sentences);
  if (spoken.size === 0 && narrated.length === 0) return text;
  const all = sentences(text);
  const kept = all.filter(
    (sentence) =>
      !spoken.has(normalizedSpeechText(sentence)) &&
      !narrated.some((said) => repeats(sentence, said)),
  );
  return kept.length === all.length ? text : kept.join(" ");
}
